import { useCallback } from 'react'

import { Button, Checkbox, Group, Stack, TextInput } from '@mantine/core'
import { hasLength, useForm } from '@mantine/form'
import { IconPlus } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'
import { PermissionApiDTO, RoleAttributesApiDTO } from '../../../codegen/generated/Api.schemas'
import { usePostApiRoles } from '../../../codegen/generated/roles'

import { UsersHelper } from '../../../helpers/Users.helper'
import { RolePermissionCards } from './RolePermissionCards'

type CreateRoleProps = {
  onCreate: (roleId: RoleAttributesApiDTO['id']) => void
}

export const CreateRole = ({ onCreate }: CreateRoleProps) => {
  const t = useTranslate()
  const form = useForm({
    initialValues: { name: '', permissions: [] as PermissionApiDTO[] },
    validate: {
      name: hasLength({ min: 3 }),
      permissions: (value: PermissionApiDTO[]) => value.length === 0,
    },
  })

  const createRoleMutation = usePostApiRoles()

  const onCreateRole = useCallback(async () => {
    try {
      const { name, permissions } = form.values
      const res = await createRoleMutation.mutateAsync({
        data: {
          name,
          permissions,
        },
      })
      onCreate(res.body.id)
    } catch (error) {
      console.error(error)
    }
  }, [form.values, createRoleMutation.mutateAsync, onCreate])

  const onPermissionsChange = useCallback(
    (p: PermissionApiDTO[]) => {
      const permissions = UsersHelper.permissionsSync(p)
      form.setValues({
        permissions,
      })
    },
    [form.setValues],
  )

  return (
    <form onSubmit={form.onSubmit(onCreateRole)}>
      <Stack>
        <TextInput
          key={form.key('name')}
          mt="md"
          w="50%"
          label={t('_generics.name')}
          placeholder={t('placeholders.name')}
          {...form.getInputProps('name')}
        />
        <Checkbox.Group
          value={form.values.permissions}
          onChange={values => onPermissionsChange(values as PermissionApiDTO[])}
          label={t('pages.users.permissions')}
        >
          <RolePermissionCards />
        </Checkbox.Group>
      </Stack>
      <Group justify="flex-end" mt="xl">
        <Button
          type="submit"
          size="compact-md"
          leftSection={<IconPlus size={14} />}
          disabled={!form.isValid()}
          loading={createRoleMutation.isLoading}
        >
          {t('_generics.create')}
        </Button>
      </Group>
    </form>
  )
}
